import type {
  IncomingPromotionListResult,
  OutgoingPromotionListResult,
  PairingListResult,
  PromotionPreviewResultPromotionBlockedSection,
} from '~/server_shared/openapi/data-contracts';

export type OutgoingPromotionRow = OutgoingPromotionListResult['items'][number];
export type IncomingPromotionRow = IncomingPromotionListResult['items'][number];
export type Pairing = PairingListResult['items'][number];
export type PromotionTypeCounts = OutgoingPromotionRow['summary'][number];
/** A configuration section left out of a preview (or a rollback's restore) because one side's export of it is
 * blocked: absent from the change list, listed with the per-offender reasons. The rollback preview's generated
 * type has the same shape. */
export type PromotionBlockedSection = PromotionPreviewResultPromotionBlockedSection;

export const PROMOTION_STATUS = {
  PENDING: 'pending',
  DEPLOYED: 'deployed',
  FAILED: 'failed',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  ROLLED_BACK: 'rolled_back',
} as const;

/** Row discriminator on a promotion request: a forward deploy vs a snapshot-based restore of a prior
 * promotion. Mirrors the backend `kind` column; only a promotion row is ever rollbackable. */
export const PROMOTION_KIND = {
  PROMOTION: 'promotion',
  ROLLBACK: 'rollback',
} as const;

/** A single approve-time conflict surfaced to the approver: the previous/expected value the promotion
 * carries (`expected_old`) vs the current target value it would overwrite (`actual_current`). Mirrors the
 * backend `PromotionConflictInfo`. */
export interface PromotionConflict {
  change_id: string;
  field_path: string;
  expected_old?: unknown;
  actual_current?: unknown;
  message: string;
}

/** One conflict as recorded in the approver's resolution decision (no `message`/`applied` — the written
 * value is recoverable from the change list). Mirrors the backend `PromotionResolvedConflict`. */
export interface PromotionResolvedConflict {
  change_id: string;
  field_path: string;
  expected_old?: unknown;
  actual_current?: unknown;
}

/** The recorded resolution decision on a resolved request: which conflicts the approver overrode vs
 * skipped. Mirrors the backend `PromotionConflictResolution`. */
export interface PromotionConflictResolution {
  overridden: PromotionResolvedConflict[];
  skipped: PromotionResolvedConflict[];
}

/** One field-level delta inside a change's `object_changes` (mirrors the Audit Log's per-field diff). */
export interface PromotionFieldDelta {
  desc?: string;
  old_value?: unknown;
  new_value?: unknown;
}

/** A single flat change in a promotion's change list (the backend serializes ConfigurationChangeV2 dicts;
 * the FE only reads these display fields and groups by `configuration_type`). */
export interface PromotionChange {
  change_id: string;
  configuration_type: string;
  change_type: string;
  object_desc?: string;
  // Structured target path (e.g. `communication_rules.autocomm_…`); rendered like the Audit Log's Object
  // column. `display_label` is the backend-provided human name of the object (from the export schema's
  // `__label_key__`); the row label shows it instead of the opaque identity-key path segment when present.
  field_path?: { path?: string; display_label?: string | null } & Record<string, unknown>;
  // Per-field old→new deltas; rendered like the Audit Log's Object Changes column.
  object_changes?: PromotionFieldDelta[];
  // Full serialized target object before/after the change.
  before_value?: unknown;
  after_value?: unknown;
  // Present (truthy) when create-time precondition detection flagged this change as a collision.
  conflict?: unknown;
  // For a REORDER change: the collection's order AFTER the apply. Not the same list as `after_value`,
  // which carries only the keys the promotion names — a member the target has and the source does not
  // is absent from it and would render as deleted, though the apply keeps it in place. Computed
  // server-side by the same function the apply uses. Empty on changes stored before it existed.
  resolved_after_value?: unknown;
  // For a REORDER change: identity-key → human label, so the reorder renderer can show names instead
  // of opaque identity keys (a reorder has no sibling item change to borrow a label from).
  key_labels?: Record<string, string>;
  // The LOBs / sub-organizations this change is attributed to. An empty or absent array for a dimension
  // means the change is not scoped to specific members of it (applies broadly). Powers the change
  // selector's scope filter.
  scope?: { lobs?: string[]; sub_organizations?: string[] };
  // The LOBs / sub-organizations pruned from this change during scope projection (cross-org promotion): members
  // named on the source but absent from the target, dropped from the promoted configuration so it applies
  // cleanly. Powers the per-change "references pruned" badge and the aggregate banner.
  pruned?: { lobs?: string[]; sub_organizations?: string[] };
  // Human-readable explanations of what projection pruned (or dropped) for this change, parallel to `pruned`.
  warnings?: string[];
  [key: string]: unknown;
}

export interface ChangeGroup {
  configuration_type: string;
  counts?: PromotionTypeCounts;
  changes: PromotionChange[];
}

/** The approver's per-conflict decision. There is deliberately no default: a conflict is undecided
 * (absent from the decisions map) until the approver explicitly picks one, and resolve is blocked until
 * every conflict is decided. */
export type ConflictChoice = 'keep' | 'override';
